import { Box, Step, StepLabel, Stepper } from '@mui/material';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { BhiDialog, SelectOption } from 'src/UIKit';
import { FormView } from 'src/components/FormPart/FormView';
import { FormErrorRecord } from 'src/components/FormPart/types';
import { Application } from 'src/modules/applications/types';
import { CreateAdminCompanyFormDialogController } from '../../controllers';
import { CreateAdminCompanyFormData } from '../../types';
import { AddSubscriptionStep, CreateCompanySubscriptionData } from './AddSubscriptionStep';
import { AddPhoneStep, PhoneStepData } from './AddPhoneStep';
import { CreateAdminCompanyForm } from './CreateAdminCompanyForm';
import { InviteUserForm, InviteUserData } from './InviteUserForm';
import { AdminCompany } from '../../types';

interface CreateAdminCompanyFormDialogProps extends CreateAdminCompanyFormDialogController {
  applicationOptions: SelectOption<string, string, Application>[];
  reloadCompanies: () => Promise<void>;
  reloadUserCompanies: () => Promise<void>;
}

const steps = ['Company Details', 'Add Phone Number', 'Add Subscription', 'Invite Company Admin'];

export const CreateAdminCompanyFormDialog: FC<CreateAdminCompanyFormDialogProps> = props => {
  const {
    isOpen,
    close,
    initFormData,
    error,
    submit,
    reloadCompanies,
    reloadUserCompanies,
    applicationOptions,
  } = props;
  const [activeStep, setActiveStep] = useState(0);
  const [companyStepData, setCompanyStepData] = useState<CreateAdminCompanyFormData | null>(null);
  const [phoneStepData, setPhoneStepData] = useState<PhoneStepData | null>(null);
  const [subscriptionStepData, setSubscriptionStepData] = useState<CreateCompanySubscriptionData | null>(
    null,
  );
  const [inviteUserStepData, setInviteUserStepData] = useState<InviteUserData | null>(null);
  const [createdCompany, setCreatedCompany] = useState<AdminCompany | null>(null);
  const [skippedSteps, setSkippedSteps] = useState<Set<number>>(new Set());

  const resetState = () => {
    setActiveStep(0);
    setCompanyStepData(null);
    setPhoneStepData(null);
    setSubscriptionStepData(null);
    setInviteUserStepData(null);
    setCreatedCompany(null);
    setSkippedSteps(new Set());
  };

  useEffect(() => {
    if (isOpen) {
      resetState();
    }
  }, [isOpen]);

  const errorList: FormErrorRecord<CreateAdminCompanyFormData>[] | undefined = useMemo(() => {
    if (error?.error === 'conflict') {
      return [{ field: 'name', message: 'Entity with this name already exists' }];
    }
  }, [error]);

  const handleNext = () => {
    setActiveStep(prevActiveStep => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1);
  };

  const handleClose = () => {
    resetState();
    close();
  };

  const isStepSkipped = (step: number) => skippedSteps.has(step);

  const selectedApplication = companyStepData?.applicationId
    ? applicationOptions.find(o => o.value === companyStepData.applicationId)?.entity
    : null;

  const mergedCompanyFormData: CreateAdminCompanyFormData | null =
    companyStepData && phoneStepData ? { ...companyStepData, ...phoneStepData } : companyStepData;

  return (
    <BhiDialog
      isOpen={isOpen}
      onClose={handleClose}
      title="Create Company"
      maxWidth={false}
      paperSx={{ width: '700px' }}
    >
      <Box p={2.5} pb={0}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label, index) => (
            <Step key={label} completed={isStepSkipped(index) ? false : undefined}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>
      {initFormData && (
        <Box p={2.5} pt={0}>
          {activeStep === 0 && (
            <FormView
              disabled={createdCompany !== null}
              formData={companyStepData || initFormData}
              component={CreateAdminCompanyForm}
              componentProps={{
                applicationOptions,
                onSubmit: (formData?: CreateAdminCompanyFormData) => {
                  if (formData) {
                    setCompanyStepData(formData);
                  }
                  handleNext();
                },
              }}
              errorList={errorList}
              onSubmit={async () => {}}
            />
          )}
          {activeStep === 1 && (
            <AddPhoneStep
              phoneStepData={phoneStepData}
              defaultUseCase={selectedApplication?.defaultUseCase ?? undefined}
              defaultSubUseCases={selectedApplication?.defaultSubUseCases ?? undefined}
              onBack={handleBack}
              onSubmit={data => {
                setPhoneStepData(data);
                handleNext();
              }}
            />
          )}
          {activeStep === 2 && (
            <AddSubscriptionStep
              applicationId={companyStepData?.applicationId}
              subscriptionData={subscriptionStepData}
              onBack={handleBack}
              onSkip={() => {
                setSubscriptionStepData(null);
                setSkippedSteps(prev => new Set(prev).add(2));
                handleNext();
              }}
              onSubmit={data => {
                setSubscriptionStepData(data);
                setSkippedSteps(prev => {
                  const next = new Set(prev);
                  next.delete(2);
                  return next;
                });
                handleNext();
              }}
              onSubscriptionDataChange={setSubscriptionStepData}
            />
          )}
          {activeStep === 3 && (
            <InviteUserForm
              onBack={handleBack}
              companyFormData={mergedCompanyFormData}
              subscriptionData={subscriptionStepData}
              inviteUserData={inviteUserStepData}
              createdCompany={createdCompany}
              onInviteUserDataChange={setInviteUserStepData}
              onCompanyCreated={setCreatedCompany}
              onSubmit={submit}
              reloadCompanies={reloadCompanies}
              reloadUserCompanies={reloadUserCompanies}
            />
          )}
        </Box>
      )}
    </BhiDialog>
  );
};
